import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useMixpanel, useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import { useDocumentReference } from '@lifeomic/phc-web-toolkit/src/hooks/useDocumentReference';
import { CitationClass } from '../types';
import RangeViewModalV2 from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/RangeViewModalV2';
import type {
  MedicalResultCitation,
  DocumentReferenceCitation,
  PubMedPaperCitation,
} from '../types';
import { getCitationClass } from './utils/getCitationClass';
import { useThrottleFn } from 'react-use';
import QueryString from 'qs';

const messages = defineMessages({
  viewPDFError: {
    id: 'agentChat.chat.viewPDFError',
    defaultMessage:
      'Error loading attachment. Check that you are using All Cohorts and try refreshing the page.',
  },
  unsupportedCitationType: {
    id: 'agentChat.chat.unsupportedCitationType',
    defaultMessage: 'Citation type not supported yet',
  },
});

interface CitationProps {
  // reference string that point to a cited resource
  reference: string;
  patient: fhir.Patient;
  onCitationProcessed: () => void;
}

export const Citation = ({
  reference,
  patient,
  onCitationProcessed,
}: CitationProps) => {
  const mixpanel = useMixpanel();
  const [showCitationModal, setShowCitationModal] = React.useState(false);
  const [medicalResult, setMedicalResult] =
    React.useState<MedicalResultCitation>({
      key: undefined,
      system: undefined,
      resourceId: undefined,
      citationType: 'MedicalResult',
      title: 'Medical Result',
    });
  const [attachmentUrl, setAttachmentUrl] = React.useState<
    string | undefined
  >();
  const { retrieveDocumentReference, retrieveAttachment } =
    useDocumentReference();

  const { formatMessage } = useIntl();
  const [notify] = useNotify();

  const onCitationProcessedMemo = React.useCallback(() => {
    onCitationProcessed();
  }, [onCitationProcessed]);

  const retrieveAttachmentFromDocRefCitation = React.useCallback(
    async (
      citationClass: DocumentReferenceCitation,
    ): Promise<string | undefined> => {
      const resourceId = citationClass.resourceId;
      if (resourceId) {
        const docRef = await retrieveDocumentReference(resourceId);
        const attachmentData = await retrieveAttachment(docRef);
        return attachmentData?.downloadUrl;
      }
    },
    [retrieveDocumentReference, retrieveAttachment],
  );

  const handleDocumentReferenceClicked = React.useCallback(
    (citationClass: DocumentReferenceCitation) => {
      retrieveAttachmentFromDocRefCitation(citationClass)
        .then(
          url => {
            const query = citationClass.query;
            if (!query) {
              setAttachmentUrl(url);
            } else {
              // query string may have other parameters, such as document type
              // but the url should only have the page parameter
              const { page } = QueryString.parse(query);
              setAttachmentUrl(`${url}#${QueryString.stringify({ page })}`);
            }
          },
          error => {
            console.error(error);
            notify(formatMessage(messages.viewPDFError), 'error');
          },
        )
        .then(onCitationProcessedMemo);
    },
    [
      retrieveAttachmentFromDocRefCitation,
      notify,
      formatMessage,
      onCitationProcessedMemo,
    ],
  );

  const handlePubMedPaperClicked = React.useCallback(
    (citationClass: PubMedPaperCitation) =>
      setAttachmentUrl(
        `https://pubmed.ncbi.nlm.nih.gov/${citationClass.pmid}/`,
      ),
    [],
  );

  const handleCitationClicked = React.useCallback(
    (citationClass: CitationClass) => {
      switch (citationClass.citationType) {
        case 'MedicalResult':
          setMedicalResult(citationClass);
          setShowCitationModal(true);
          onCitationProcessedMemo();
          break;
        case 'DocumentReference':
          handleDocumentReferenceClicked(citationClass);
          break;
        case 'PubMedPaper':
          handlePubMedPaperClicked(citationClass);
          onCitationProcessedMemo();
          break;
        default:
          notify(formatMessage(messages.unsupportedCitationType), 'warning');
          onCitationProcessedMemo(); // Call this for unsupported types too
      }
      mixpanel.track('zora_citation_opened', {
        type: citationClass.citationType,
        title: citationClass.title,
      });
    },
    [
      mixpanel,
      onCitationProcessedMemo,
      handleDocumentReferenceClicked,
      handlePubMedPaperClicked,
      notify,
      formatMessage,
    ],
  );

  useThrottleFn(
    (reference: string, showCitationModal) => {
      if (reference && !showCitationModal) {
        const citationClass = getCitationClass(reference);
        handleCitationClicked(citationClass);
      }
    },
    1000,
    [reference, showCitationModal],
  );

  React.useEffect(() => {
    // open attachment url in a new window
    if (attachmentUrl) {
      window.open(attachmentUrl);
    }
  }, [attachmentUrl, setAttachmentUrl]);

  React.useEffect(() => {
    if (medicalResult.key) {
      setShowCitationModal(true);
    }
  }, [medicalResult, setShowCitationModal]);

  return (
    <>
      {showCitationModal && (
        <RangeViewModalV2
          isOpen={showCitationModal}
          onClose={() => {
            // reset medical result to make the same citation clickable
            setMedicalResult({
              key: undefined,
              system: undefined,
              resourceId: undefined,
              citationType: 'MedicalResult',
              title: 'Medical Result',
            });
            setShowCitationModal(false);
          }}
          dimension={{
            codes: [medicalResult],
          }}
          patient={patient}
          initialSelectedResourceId={medicalResult.resourceId}
        />
      )}
    </>
  );
};
